import React, { useState } from 'react';
import {
  Controller,
  FieldError,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Button,
  Checkbox,
  LoadingButton,
  SelectionControlLabel,
  TextField,
  Typography,
} from '@one-thd/sui-atomic-components';
import { TextToConfirm } from '@one-thd/sui-icons';
import { useConfigService } from '@thd-nucleus/experience-context';
import { FormTitle } from '../../shared/form-title/form-title';
import { validationSchema } from './validation';
import { getHyphenatedPhoneNumber } from '../../../utils/masking-field';
import { ProHouseAccountInfo } from './pro-house-account-info/pro-house-account-info';
import { InfoPopover } from '../../shared/popover/popover';
import { T2CTermsAndConditions } from '../remote-purchases-auth/t2c-terms-conditons';
import { onTooltipClick } from '../../../utils/remote-purchases';
import { IPaymentCardCommonData } from '../../../models/common/payments';
import {
  IAlertMessage,
  TAlertStatus,
} from '../../../models/common/alert-messages';
import { usePayments } from '../../../hooks/use-payments';
import { useCustomerInfo } from '../../../hooks/use-customerInfo';
import { getT2CSuccessMessage } from '../../../utils/pro-house-account';
import { usePhaUnenrollT2cConfirmModal } from '../../../hooks/use-pha-unenroll-t2c-confirm-modal';
import {
  TApprovers,
  TProHouseAccountAction,
} from '../../../models/b2b/payments';
import {
  UnenrollT2CConfirmModalBodyText,
  UnenrollT2CConfirmModalHeaderText,
} from '../../../constants/pro-house-account';
import {
  ERR_GENERIC,
  ERR_MAX_T2C_TENDER_LIMIT,
} from '../../../constants/response-error-messages';
import {
  removeNonDigitsFromString,
  formatRedactedPhoneNumbers,
  extractPhoneNumbersFromErrorMessage,
  getDuplicateT2cErrorMessage,
} from '../../../utils/general';
import {
  MAX_T2C_TENDER_LIMIT_REACHED_CODE,
  T2C_NUMBER_ASSOCIATED_ANOTHER_ACCOUNT,
} from '../../../constants/error-codes';
import { getPaymentsErrorCode } from '../../../utils/errors';
import { MultipleApproversSection } from '../remote-purchases-auth/multiple-approvers-section';
import { ptcRemotePurchasesPtcPostPageErrorAnalytics } from '../../../utils/payments-analytics';

export type TProHouseAccountsAuthForm = {
  t2cCheckbox: boolean;
  phoneNumber: string;
  additionalApprovers: TApprovers;
  hasMultipleApprovers: boolean;
};

export interface IProHouseAccountAuthFormProps {
  onCloseForm: () => void;
  onSuccess: (
    action: TProHouseAccountAction,
    message: React.ReactChild,
  ) => void;
  onFailure: (
    action: TProHouseAccountAction,
    message: React.ReactChild,
  ) => void;
  card: IPaymentCardCommonData;
}

const getTextFieldStatus = (error: FieldError | undefined) =>
  error ? 'error' : undefined;

export const ProHouseAccountAuthForm = ({
  onCloseForm,
  onSuccess,
  onFailure,
  card,
}: IProHouseAccountAuthFormProps) => {
  const [formAlertMessage, setFormAlertMessage] =
    useState<IAlertMessage | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enrollPHAT2C, unenrollPHAT2C, t2cMultipleApproverContacts } =
    usePayments();
  const { svocID } = useCustomerInfo();

  const [openConfirmModal] = usePhaUnenrollT2cConfirmModal({
    selectedCard: card,
  });
  const { t2cNumber, paymentId } = card;
  const t2cStatus = card.t2cStatus?.toLowerCase();
  const t2cPending = t2cStatus === 'pending';
  const t2cEnrolled = t2cStatus === 'enrolled';
  const isT2CCurrentlyActive = t2cPending || t2cEnrolled;
  const activeT2COnFile = card.activeT2COnFile;
  const defaultT2CNumber = t2cNumber || activeT2COnFile || '';

  const multipleApproversAllowedSvocIDs = useConfigService(
    'T2C_MULTIPLE_APPROVERS_ALLOWED_SVOCIDS',
  );
  const isSvocIDAllowedForMultipleAprovers =
    typeof multipleApproversAllowedSvocIDs === 'string' &&
    multipleApproversAllowedSvocIDs.includes(svocID);

  const t2cMultipleApproversEnabled =
    !!useConfigService('T2C_MULTIPLE_APPROVERS_ENABLED') ||
    isSvocIDAllowedForMultipleAprovers;
  const isProTradeCreditT2CEnabled = !useConfigService('DISABLE_PTC_T2C');
  const hasMultipleApproversOnFile = !!(
    t2cMultipleApproverContacts && t2cMultipleApproverContacts.length > 0
  );

  const defaultValues: TProHouseAccountsAuthForm = {
    t2cCheckbox: isT2CCurrentlyActive,
    phoneNumber: getHyphenatedPhoneNumber(defaultT2CNumber),
    additionalApprovers: hasMultipleApproversOnFile
      ? t2cMultipleApproverContacts.map(
          ({ fullName, t2cNumber: number, status }) => ({
            fullName: fullName || '',
            t2cNumber: getHyphenatedPhoneNumber(number),
            status,
          }),
        )
      : [
          {
            fullName: '',
            t2cNumber: getHyphenatedPhoneNumber(defaultT2CNumber),
            status: null,
          },
        ],
    hasMultipleApprovers: t2cMultipleApproversEnabled,
  };

  const formMethods = useForm<TProHouseAccountsAuthForm>({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors: formErrors, isDirty },
  } = formMethods;

  const watchT2cCheckbox = watch('t2cCheckbox');
  const shouldShowMultipleApprovers = watch('hasMultipleApprovers');

  const handleSuccess = (action: TProHouseAccountAction) => {
    const text = getT2CSuccessMessage(action);
    onSuccess(action, text);
  };

  const handleFailure = (
    action: TProHouseAccountAction,
    optionalMessage?: React.ReactChild,
  ) => {
    onFailure(action, optionalMessage || ERR_GENERIC.message);
  };

  const unenroll = ({ additionalApprovers }: TProHouseAccountsAuthForm) => {
    return unenrollPHAT2C({
      paymentId,
      t2cApprovers: shouldShowMultipleApprovers ? additionalApprovers : null,
    })
      .then((success: boolean) => {
        if (success) {
          return handleSuccess(TProHouseAccountAction.UNENROLL);
        }
        return handleFailure(TProHouseAccountAction.UNENROLL);
      })
      .catch(() => {
        return handleFailure(TProHouseAccountAction.UNENROLL);
      });
  };

  const enroll = ({
    phoneNumber,
    additionalApprovers,
  }: TProHouseAccountsAuthForm) => {
    return enrollPHAT2C({
      t2cNumber: removeNonDigitsFromString(phoneNumber),
      paymentId,
      locType: card?.locType,
      isResend: false,
      t2cApprovers: shouldShowMultipleApprovers ? additionalApprovers : null,
    })
      .then((success: boolean) => {
        if (success) {
          return handleSuccess(TProHouseAccountAction.ENROLL);
        }
        return handleFailure(TProHouseAccountAction.ENROLL);
      })
      .catch((error) => {
        const errorCode = getPaymentsErrorCode(error);
        if (errorCode === MAX_T2C_TENDER_LIMIT_REACHED_CODE) {
          ptcRemotePurchasesPtcPostPageErrorAnalytics(
            ERR_MAX_T2C_TENDER_LIMIT.message,
          );
          handleFailure(
            TProHouseAccountAction.ENROLL,
            ERR_MAX_T2C_TENDER_LIMIT.message,
          );
          return;
        }

        if (errorCode === T2C_NUMBER_ASSOCIATED_ANOTHER_ACCOUNT) {
          const phoneNumbers = error.errors?.[0]?.errorMessage
            ? extractPhoneNumbersFromErrorMessage(error.errors[0].errorMessage)
            : [];

          const phoneNumbersText = formatRedactedPhoneNumbers(phoneNumbers);
          const errorMessage = getDuplicateT2cErrorMessage(
            phoneNumbers,
            phoneNumbersText,
          );
          ptcRemotePurchasesPtcPostPageErrorAnalytics(errorMessage);
          setFormAlertMessage({
            alertStatus: TAlertStatus.ERROR,
            text: errorMessage,
          });
          return;
        }

        handleFailure(TProHouseAccountAction.ENROLL);
      });
  };

  const onSubmitForm: SubmitHandler<TProHouseAccountsAuthForm> = (
    values: TProHouseAccountsAuthForm,
  ) => {
    setFormAlertMessage(null);
    if (!isDirty) {
      onCloseForm();
      return;
    }

    setIsSubmitting(true);

    // Did they uncheck the box?
    if (!values.t2cCheckbox) {
      const confirmModalProps = {
        bodyText: UnenrollT2CConfirmModalBodyText,
        headerText: UnenrollT2CConfirmModalHeaderText,
      };
      openConfirmModal(confirmModalProps)
        .then((confirmed: boolean) => {
          if (confirmed) {
            return unenroll(values);
          }
          onCloseForm();
          return Promise.resolve();
        })
        .finally(() => {
          setIsSubmitting(false);
        });
      return;
    }

    enroll(values).finally(() => {
      setIsSubmitting(false);
    });
  };

  return (
    <div className="sm:sui-max-w-[485px] sui-flex sui-flex-col sui-gap-10">
      <FormTitle
        title="Authorize pro trade credit for Remote Purchases"
        actionLabel="Cancel"
        onActionClick={() => onCloseForm()}
      />
      <Typography>
        Let your team members check out at The Home Depot using Pro Trade
        Credit. Approve and verify in-store purchases via text message.
      </Typography>
      <ProHouseAccountInfo proHouseAccount={card} />
      <div>
        {!isProTradeCreditT2CEnabled && (
          <Alert status="warning">
            <span>
              Text2Confirm has been restored for use with Pro Trade Credit and
              is ready to use at your local Home Depot store. Currently remote
              purchase enrollment or updates are not available for Pro Trade
              Credit.
              <br />
              Questions? Contact your Sales Representative.
            </span>
          </Alert>
        )}
        {isProTradeCreditT2CEnabled && (
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmitForm)}>
              <div className="sui-flex sui-gap-4">
                <div className="sui-mt-2 sui-w-6 sui-h-6">
                  <TextToConfirm />
                </div>
                <div>
                  <SelectionControlLabel
                    label={
                      <div className="sui-flex sui-items-center">
                        <Typography weight="bold">Text2Confirm</Typography>
                      </div>
                    }
                  >
                    <Controller
                      name="t2cCheckbox"
                      control={control}
                      render={({
                        field: { value, name, onChange, disabled, onBlur },
                      }) => (
                        <Checkbox
                          name={name}
                          onChange={onChange}
                          disabled={disabled}
                          onBlur={onBlur}
                          checked={value}
                          data-testid="t2c-checkbox"
                          inputAttributes={{ 'aria-label': 'controlled' }}
                        />
                      )}
                    />
                  </SelectionControlLabel>
                  <div className="sui-ml-[-16px] sui-inline-block">
                    <InfoPopover
                      title="Approve by Text2Confirm"
                      iconSize="small"
                      onIconClick={() => onTooltipClick('T2C')}
                    >
                      <div className="sui-w-[260px] sui-text-xs">
                        Receive a text message with checkout details and reply
                        to approve the in-store purchase.
                      </div>
                    </InfoPopover>
                  </div>
                  {watchT2cCheckbox && (
                    <>
                      {shouldShowMultipleApprovers ? (
                        <div
                          className="sui-mt-6"
                          data-testid="pha-multi-approver-section"
                        >
                          <MultipleApproversSection />
                        </div>
                      ) : (
                        <>
                          <div className="sui-my-6">
                            <Typography>
                              We’ll send a text message to the phone number
                              provided below.
                            </Typography>
                          </div>
                          <Controller
                            name="phoneNumber"
                            control={control}
                            render={({
                              field: {
                                ref,
                                name,
                                onChange,
                                value,
                                ...restFields
                              },
                            }) => (
                              <TextField
                                label="Mobile Phone Number*"
                                value={value}
                                name={name}
                                fullWidth
                                helpMessage="This number is used for all cards authorized for Text2Confirm."
                                status={getTextFieldStatus(
                                  formErrors.phoneNumber,
                                )}
                                statusMessage={formErrors.phoneNumber?.message}
                                InputProps={{
                                  inputProps: { 'data-qm-mask': '' },
                                  onChange: (
                                    event: React.ChangeEvent<HTMLInputElement>,
                                  ) => {
                                    onChange(
                                      getHyphenatedPhoneNumber(
                                        event.currentTarget.value || '',
                                      ),
                                    );
                                  },
                                  ...restFields,
                                }}
                              />
                            )}
                          />
                        </>
                      )}

                      <T2CTermsAndConditions paymentType="Pro Trade Credit" />
                    </>
                  )}
                  {formAlertMessage && (
                    <div
                      className="sui-mt-10"
                      data-testid="general-error-message"
                    >
                      <Alert status={formAlertMessage.alertStatus}>
                        {formAlertMessage.text}
                      </Alert>
                    </div>
                  )}
                </div>
              </div>
              <div className="sui-mt-10 sui-grid sui-grid-cols-1 sui-gap-5 sm:sui-grid-cols-2">
                <div className="sui-order-last sm:sui-order-first">
                  <Button fullWidth variant="secondary" onClick={onCloseForm}>
                    Cancel
                  </Button>
                </div>
                <LoadingButton
                  loading={isSubmitting}
                  fullWidth
                  variant="primary"
                  type="submit"
                >
                  Save
                </LoadingButton>
              </div>
            </form>
          </FormProvider>
        )}
      </div>
    </div>
  );
};
